<template>
  <Modal name="LINK_CHECKLIST_MODAL" :title="title" :width="600" actions>
    <template v-slot:body>
      <h-loading v-if="isLoading" />

      <div v-else>
        <v-form ref="form" v-model="formValid" lazy-validation @submit.prevent>
          <v-autocomplete
            v-model="form.real_estate_id"
            :items="realEstates"
            :label="$t('userChecklistModal.realEstate')"
            :hint="$t('userChecklistModal.realEstateHint')"
            :search-input.sync="realEstateSearch"
            :loading="isRealEstateLoading"
            :no-data-text="$t('appointment.filters.noData')"
            item-text="name"
            item-value="id"
            :no-filter="true"
            persistent-hint
            hide-selected
            clearable
            outlined
          />

          <v-spacer class="my-4" />

          <v-autocomplete
            v-model="form.appointment_type"
            :items="translatedAppointmentTypes"
            :label="$t('dashboard.tabFilter.label.serviceType')"
            item-text="text"
            item-value="value"
            :filter="accentInsensitiveFilter"
            :no-data-text="$t('appointment.filters.noData')"
            hide-details="auto"
            hide-selected
            clearable
            outlined
            required
          />

          <v-spacer class="my-4" />

          <v-autocomplete
            v-model="selectedChecklist"
            :items="checklistsNormalized"
            :label="$t('pages.title.modelsInspections')"
            item-text="name"
            item-value="value"
            :return-object="true"
            :search-input.sync="searchText"
            :no-filter="true"
            :cache-items="false"
            :loading="isLoading"
            :no-data-text="$t('appointment.filters.noData')"
            hide-details="auto"
            hide-selected
            clearable
            outlined
            required
          />
          <small v-if="selectedChecklist && !selectedChecklist.code" class="red--text">
            {{ $t('common.warning') }}: este modelo não possui <strong>code</strong>. Não é possível vincular.
          </small>

          <!-- Any model can require a base report: the comparison adopts the base report's own model -->
          <template v-if="baseReportEnabled">
            <v-spacer class="my-4" />

            <div class="d-flex align-center">
              <v-switch
                v-model="form.base_report_required"
                :label="$t('userChecklistModal.baseReportRequired')"
                class="mt-0 mr-2"
                hide-details
                :ripple="false"
                inset
              />
              <InformationTooltip :message="$t('userChecklistModal.baseReportRequiredInfo')" />
            </div>
          </template>
        </v-form>
      </div>
    </template>
    <template v-slot:actions>
      <div class="d-flex">
        <v-btn
          @click="close"
          :disabled="isSubmitting"
          class="mr-5"
          elevation="0"
          large
          text
        >
          {{ $t('userChecklistModal.cancel') }}
        </v-btn>
        <v-btn
          @click="submit"
          :loading="isSubmitting"
          :disabled="isSubmitting || !formValid"
          color="primary"
          elevation="0"
          large
        >
          {{ $t('userChecklistModal.confirm') }}
        </v-btn>
      </div>
    </template>
  </Modal>
</template>

<script>
import Modal from '@/components/app/Modal'
import InformationTooltip from '@/components/app/InformationTooltip'
import { mapActions, mapGetters } from 'vuex'
import { normalizeTextSearch } from '@/util/helpers'
import { searchRealEstates } from '../service/appointmentTypeChecklistService'
import { isBaseReportEnabled } from '@/util/featureFlags'

export default {
  name: 'LinkChecklistModal',

  components: {
    Modal,
    InformationTooltip,
  },

  props: {
    checklistConfigCode: String,
  },

  data() {
    return {
      formValid: true,
      isSubmitting: false,

      searchText: '',
      _timer: null,

      realEstates: [],
      realEstateSearch: '',
      realEstateTimer: null,
      isRealEstateLoading: false,

      form: {
        real_estate_id: null,
        appointment_type: null,
        checklist_config_code: '',
        base_report_required: false,
      },

      selectedChecklist: null,

      title: this.$t('customer.label.linkCustomerType'),

      baseReportEnabled: isBaseReportEnabled(),
    }
  },

  computed: {
    ...mapGetters({
      checklists: 'checklists',
      filteredAppointmentTypes: 'filteredAppointmentTypes',
      isLoading: 'loading/isLoading',
      activeModal: 'activeModal',
    }),

    checklistsNormalized() {
      const arr = Array.isArray(this.checklists) ? this.checklists : []
      return arr
        .filter(i => i && i.code)
        .map(i => ({ ...i, value: i.code }))
    },

    translatedAppointmentTypes() {
      return this.filteredAppointmentTypes.map((type) => {
        const value = typeof type === 'string' ? type : type.value
        return {
          text: this.$t(`appointment.type.${value}`),
          value,
        }
      })
    },

    isModalOpen() {
      return this.activeModal === 'LINK_CHECKLIST_MODAL'
    },
  },

  watch: {
    isModalOpen(val) {
      if (val) {
        this.initialize()
      } else {
        this.$emit('close')
        this.$emit('refresh')
      }
    },

    // A real estate can only be linked to the types of its contract that already have a default model
    'form.real_estate_id'(val) {
      this.form.appointment_type = null
      this.loadAppointmentTypes(val)
    },

    realEstateSearch(val) {
      const selected = this.realEstates.find(item => item.id === this.form.real_estate_id)
      if (selected && selected.name === val) return

      clearTimeout(this.realEstateTimer)
      this.realEstateTimer = setTimeout(() => this.loadRealEstates(val), 500)
    },

    searchText(val) {
      if (this._timer) clearTimeout(this._timer)
      this._timer = setTimeout(() => {
        this.loadChecklists({
          page: 1,
          size: 25,
          search: val && val.trim() ? val.trim() : undefined,
        })
      }, 300)
    },
  },

  methods: {
    ...mapActions([
      'loadChecklists',
      'loadAppointmentTypes',
      'linkServiceType',
      'closeModal',
      'showErrorMessage',
    ]),

    close() {
      this.$store.dispatch('closeModal', 'LINK_CHECKLIST_MODAL')
    },

    accentInsensitiveFilter(item, queryText, itemText) {
      const query = normalizeTextSearch(queryText).trim()
      if (!query) return true

      const target = normalizeTextSearch(itemText || item?.text || item?.value)
      return target.includes(query)
    },

    async loadRealEstates(search) {
      this.isRealEstateLoading = true
      try {
        const { data } = await searchRealEstates(search && search.trim() ? search.trim() : undefined)
        this.realEstates = Array.isArray(data?.result) ? data.result : []
      } catch ({ data }) {
        this.showErrorMessage(data?.message)
      } finally {
        this.isRealEstateLoading = false
      }
    },

    resetForm() {
      this.form = {
        real_estate_id: null,
        appointment_type: null,
        checklist_config_code: this.checklistConfigCode || '',
        base_report_required: false,
      }
      this.formValid = true
      this.searchText = ''
      this.realEstateSearch = ''
      this.selectedChecklist = null
    },

    async initialize() {
      this.resetForm()
      await this.loadChecklists({ hasCode: true, page: 1, size: 25 })
      await this.loadAppointmentTypes()
      await this.loadRealEstates()
    },

    async submit() {
      const form = this.$refs.form
      if (!form || !form.validate()) return

      if (!this.selectedChecklist || !this.selectedChecklist.code) {
        this.showErrorMessage(this.$t('common.requiredField') + ': code')
        return
      }

      this.form.checklist_config_code = this.selectedChecklist.code

      this.isSubmitting = true
      try {
      await this.linkServiceType(this.form)
      this.close()
      } finally {
        this.isSubmitting = false
      }
    },
  },
}
</script>
